/**
 * Per-year holiday cache, populated lazily on first read of any year via
 * `GET /api/calendar/holidays/{year}`. Admin POST/DELETE handlers call
 * `invalidateYear(year)` after a successful response so the next render
 * sees the new list.
 *
 * The cache returns a stable reference per year so consumers (the
 * `useYearExtras` hook in particular) do not re-render on identity churn
 * — same content in, same array out.
 */
import { parseYmKey } from "@shared/calendar";
import type { ExtrasList, Holiday, MonthDay } from "@shared/types";
import { useSyncExternalStore } from "react";

import { apiFetch } from "./api";

export type { Holiday, ExtrasList } from "@shared/types";

export type HolidayList = ReadonlyArray<Holiday>;

const EMPTY: HolidayList = Object.freeze([]);
const EMPTY_EXTRAS: ExtrasList = Object.freeze([]);

const data = new Map<number, HolidayList>();
const extrasView = new Map<number, ExtrasList>();
const inFlight = new Set<number>();
const listeners = new Map<number, Set<() => void>>();

function notifyYear(year: number): void {
  const set = listeners.get(year);
  if (!set) return;
  for (const fn of set) fn();
}

function buildExtrasView(list: HolidayList): ExtrasList {
  const out: MonthDay[] = [];
  for (const h of list) {
    if (h.kind === "extra") out.push({ month: h.month, day: h.day });
  }
  return Object.freeze(out);
}

/** Cheap structural equality on the extras axis only (the only field
 *  consumed by `useSyncExternalStore` snapshots). Rendering callers
 *  re-render on identity change of the extras array, so a refetch that
 *  returned the same list shouldn't poke them. */
function sameExtras(a: ExtrasList, b: ExtrasList): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const x = a[i] as MonthDay;
    const y = b[i] as MonthDay;
    if (x.month !== y.month || x.day !== y.day) return false;
  }
  return true;
}

function setYear(year: number, list: HolidayList): void {
  const nextExtras = buildExtrasView(list);
  const prevExtras = extrasView.get(year);
  data.set(year, list);
  extrasView.set(year, nextExtras);
  if (!prevExtras || !sameExtras(prevExtras, nextExtras)) {
    notifyYear(year);
  }
}

async function fetchYear(year: number): Promise<void> {
  if (inFlight.has(year)) return;
  inFlight.add(year);
  try {
    const res = await apiFetch(`/api/calendar/holidays/${year}`);
    if (!res.ok) return;
    const body = await res.json();
    if (!Array.isArray(body)) return;
    setYear(year, body as Holiday[]);
  } catch {
    // Cache stays empty; subsequent calls retry.
  } finally {
    inFlight.delete(year);
  }
}

export function getYearHolidays(year: number): HolidayList {
  const cached = data.get(year);
  if (cached) return cached;
  void fetchYear(year);
  return EMPTY;
}

/**
 * Imperative read of the per-year holiday cache for a `YYYY-MM` key,
 * filtered to admin-managed extras. Used at hours↔percent conversion
 * call sites that aren't React renders (event handlers, CSV exports).
 * On cache miss: queues a fetch and returns an empty array for the
 * current frame; the next render after the fetch resolves picks up the
 * populated value.
 */
export function extrasForYmKey(ymKey: string): ExtrasList {
  const parsed = parseYmKey(ymKey);
  if (!parsed) return [];
  return getYearHolidays(parsed.y)
    .filter((h) => h.kind === "extra")
    .map((h) => ({ month: h.month, day: h.day }));
}

export function invalidateYear(year: number): void {
  data.delete(year);
  extrasView.delete(year);
  void fetchYear(year);
}

export function setYearHolidays(year: number, list: HolidayList): void {
  setYear(year, list);
}

function subscribeYear(year: number): (cb: () => void) => () => void {
  return (cb) => {
    let set = listeners.get(year);
    if (!set) {
      set = new Set();
      listeners.set(year, set);
    }
    set.add(cb);
    // Trigger fetch on subscribe if absent.
    if (!data.has(year) && !inFlight.has(year)) void fetchYear(year);
    return () => {
      const s = listeners.get(year);
      if (!s) return;
      s.delete(cb);
      if (s.size === 0) listeners.delete(year);
    };
  };
}

export function useYearExtras(year: number): ExtrasList {
  return useSyncExternalStore(
    subscribeYear(year),
    () => extrasView.get(year) ?? EMPTY_EXTRAS,
    () => EMPTY_EXTRAS,
  );
}

export function useYearHolidays(year: number): HolidayList {
  return useSyncExternalStore(
    subscribeYear(year),
    () => data.get(year) ?? EMPTY,
    () => EMPTY,
  );
}

export function _resetForTests(): void {
  data.clear();
  extrasView.clear();
  inFlight.clear();
  listeners.clear();
}
